import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import moment from "moment";
import PrettyContainer from "../../prettyContainer/PrettyContainer";
import {
  getProfile,
  getAllApplications,
  updateMyAccount,
  updatePassword,
} from "../../../helpers/api";
import { getRemaining } from "../../../helpers/dueDate";

const AZUL = "#3790ed";
const AZUL_OSCURO = "#1c6fc4";
const MUTED = "#6b7a90";

export default function MyAccountPage() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [catalog, setCatalog] = useState([]);

  // Editable account fields
  const [personalEmail, setPersonalEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [savingAcc, setSavingAcc] = useState(false);
  const [accMsg, setAccMsg] = useState({ type: "", text: "" });

  // Password
  const [pass, setPass] = useState("");
  const [rpass, setRpass] = useState("");
  const [savingPass, setSavingPass] = useState(false);
  const [passMsg, setPassMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    getProfile()
      .then((data) => {
        setProfile(data);
        setPersonalEmail(data.personalEmail || "");
        setPhoneNumber(data.phoneNumber || "");
        setLoading(false);
      })
      .catch(() => setLoading(false));
    getAllApplications()
      // El backend responde { applications: [...] }, no un arreglo pelado.
      .then((res) => setCatalog(Array.isArray(res) ? res : (res && res.applications) || []))
      .catch(() => setCatalog([]));
  }, []);

  const guardarCuenta = () => {
    setAccMsg({ type: "", text: "" });
    setSavingAcc(true);
    updateMyAccount({ personalEmail, phoneNumber })
      .then(() => {
        setSavingAcc(false);
        setAccMsg({ type: "success", text: "Your account was updated." });
      })
      .catch((msg) => {
        setSavingAcc(false);
        setAccMsg({ type: "warning", text: typeof msg === "string" ? msg : "Could not save." });
      });
  };

  const guardarClave = () => {
    setPassMsg({ type: "", text: "" });
    if (!pass || pass.length < 8) {
      setPassMsg({ type: "warning", text: "Password should be at least 8 characters." });
      return;
    }
    if (pass !== rpass) {
      setPassMsg({ type: "warning", text: "Passwords don't match." });
      return;
    }
    setSavingPass(true);
    updatePassword(pass)
      .then(() => {
        setSavingPass(false);
        setPass("");
        setRpass("");
        setPassMsg({ type: "success", text: "Password changed." });
      })
      .catch((msg) => {
        setSavingPass(false);
        setPassMsg({ type: "warning", text: typeof msg === "string" ? msg : "Could not change password." });
      });
  };

  const appDe = (id) => catalog.find((a) => Number(a.id) === Number(id));

  const venceApp = (id) => {
    const e = (profile?.applicationDates || []).find((x) => Number(x.app) === Number(id));
    return e && e.vence ? e.vence : profile?.dueDate || null;
  };

  const chipEstado = (level) => {
    const colores = {
      ok: ["#e6f6ec", "#1a8a4a"],
      warning: ["#fff4e0", "#b7791f"],
      critical: ["#fdeaea", "#c0392b"],
      expired: ["#fdeaea", "#c0392b"],
      none: ["#eef1f5", "#6b7a90"],
    };
    const [bg, col] = colores[level] || colores.none;
    return { background: bg, color: col };
  };

  if (loading) {
    return (
      <PrettyContainer tittle="MY ACCOUNT">
        <div style={{ textAlign: "center", padding: "24px", color: MUTED }}>
          <Spinner animation="border" role="status" size="sm" /> Loading…
        </div>
      </PrettyContainer>
    );
  }

  const apps = (profile?.applications || []).map(Number);
  // Solo las que tienen nombre real en el catalogo: nada de "App 10/11" que el
  // cliente no entiende.
  const conocidas = apps.filter((id) => appDe(id));

  return (
    <PrettyContainer tittle="MY ACCOUNT">
      <div style={{ paddingTop: "0.6em" }}>
        {/* ---------- Account details ---------- */}
        <div style={estilos.card}>
          <div style={estilos.h2}>Account details</div>

          <Form.Group className="mb-3">
            <Form.Label style={estilos.label}>Login email</Form.Label>
            <Form.Control type="text" value={profile?.username || ""} disabled readOnly />
            <div style={estilos.hint}>This is the email you sign in with. Contact us to change it.</div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={estilos.label}>Contact email</Form.Label>
            <Form.Control
              type="email"
              placeholder="you@example.com"
              value={personalEmail}
              onChange={(e) => setPersonalEmail(e.target.value)}
            />
            <div style={estilos.hint}>Where we send the button to release your devices.</div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={estilos.label}>Phone / WhatsApp</Form.Label>
            <Form.Control
              type="text"
              placeholder="+503 7000 0000"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </Form.Group>

          {accMsg.text && <Alert variant={accMsg.type}>{accMsg.text}</Alert>}
          <button onClick={guardarCuenta} disabled={savingAcc} style={estilos.boton}>
            {savingAcc && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}{" "}
            Save changes
          </button>
        </div>

        {/* ---------- Password ---------- */}
        <div style={estilos.card}>
          <div style={estilos.h2}>Change password</div>
          <Form.Group className="mb-3">
            <Form.Label style={estilos.label}>New password</Form.Label>
            <Form.Control
              type="password"
              placeholder="******"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={estilos.label}>Repeat password</Form.Label>
            <Form.Control
              type="password"
              placeholder="******"
              value={rpass}
              onChange={(e) => setRpass(e.target.value)}
            />
          </Form.Group>
          {passMsg.text && <Alert variant={passMsg.type}>{passMsg.text}</Alert>}
          <button onClick={guardarClave} disabled={savingPass} style={estilos.boton}>
            {savingPass && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}{" "}
            Save password
          </button>
        </div>

        {/* ---------- Subscriptions ---------- */}
        <div style={estilos.card}>
          <div style={estilos.h2}>My subscriptions</div>
          {conocidas.length === 0 ? (
            <div style={{ color: MUTED }}>You don't have any active subscriptions.</div>
          ) : (
            conocidas.map((id) => {
              const app = appDe(id);
              const info = getRemaining(venceApp(id));
              const venc = venceApp(id);
              return (
                <div key={id} style={estilos.subRow}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                    {app.img ? (
                      <img src={app.img} alt="" style={estilos.logo} />
                    ) : (
                      <div style={estilos.logo} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: "#1d2735" }}>{app.tittle || app.title}</div>
                      <div style={{ color: MUTED, fontSize: "0.85rem" }}>
                        {venc ? `Renews / expires ${moment(venc).format("MMMM D, YYYY")}` : "No expiry date"}
                      </div>
                    </div>
                  </div>
                  <span style={{ ...estilos.chip, ...chipEstado(info.level) }}>{info.text}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </PrettyContainer>
  );
}

const estilos = {
  card: {
    background: "#ffffff",
    border: "1px solid #e6ebf2",
    borderRadius: "16px",
    padding: "18px 20px",
    marginBottom: "14px",
    boxShadow: "0 1px 2px rgba(16,21,28,0.04)",
  },
  h2: {
    fontWeight: 800,
    color: "#1d2735",
    fontSize: "1.05rem",
    marginBottom: "14px",
  },
  label: { fontWeight: 600, color: "#3a4759", marginBottom: "4px" },
  hint: { color: "#8895a7", fontSize: "0.82rem", marginTop: "4px" },
  boton: {
    background: `linear-gradient(135deg, ${AZUL} 0%, ${AZUL_OSCURO} 100%)`,
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    padding: "11px 22px",
    fontWeight: 700,
    fontSize: "0.92rem",
    cursor: "pointer",
  },
  subRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "12px 0",
    borderTop: "1px solid #eef1f5",
  },
  chip: {
    fontSize: "0.75rem",
    fontWeight: 700,
    padding: "4px 11px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
  },
  logo: {
    width: "38px",
    height: "38px",
    borderRadius: "9px",
    objectFit: "cover",
    background: "#eef1f5",
    flexShrink: 0,
    border: "1px solid #e6ebf2",
  },
};
